import { computed, Ref, ref, set, watch } from "@vue/composition-api";
import {
  ContentFile,
  InputContentFileCreate,
  InputContentFileUpdate,
} from "@scrile/api-provider/dist/api/ContentFilesProvider";
import {
  ContentFeedType,
  ContentFeedTypeEnum,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { UploadFileData } from "@scrile/tools/dist/lib/FileUploader";
import useBlobUrl from "@/hooks/useBlobUrl";
import providers from "@/lib/api";
import { getBlobMetadata, getFileMimeType, isAudioFile, isVideoFile } from "@/lib/fileHelpers";
import FileUploader, { calculateProgress, showDefaultUploadError } from "@/lib/FileUploader";
import logger from "@/modules/logger";
import { AttachmentFile, PostFileData } from "@/types";
import { AddingFilesOptions, FileUploadData, GenerateFileData, UploadedFileData } from "../types";

const TEMP_FILE_PREFIX = "file__";
const TEMP_FILE_POSTFIX = "__random-";

export function extractUid(str: string): string {
  if (!str.startsWith(TEMP_FILE_PREFIX)) {
    return str;
  }

  const regex = /^file__(.+?)(?:__random-\d+)?$/;
  const match = str.match(regex);

  return match ? match[1] : str;
}

function isFileUploadData(data: any): data is FileUploadData {
  return typeof data.file !== "undefined";
}

function getTemporaryContentFile(data: Partial<ContentFile>): ContentFile {
  return {
    id: "0",
    mediaType: "",
    name: "",
    publicAccess: false,
    setId: "",
    size: 0,
    width: 0,
    height: 0,
    sort: 0,
    title: "",
    uploadTime: "",
    urlPart: "",
    uuid: "0",
    processingStatus: "OK",
    duration: 0,
    metadata: {},
    thumbnailType: null,
    ...data,
  };
}

interface UsePostFilesOptions {
  ignoreRemovedUploadSuccess?: boolean;
  keepUploaderQueueOnError?: boolean;
}

interface DeferredFileCreate {
  fileId: string;
  run: () => Promise<void>;
}

export default function usePostFiles(
  postId: Ref<string>,
  postType: Ref<ContentFeedType>,
  defferChange: Ref<boolean>,
  options: UsePostFilesOptions = {}
) {
  const { ignoreRemovedUploadSuccess = false, keepUploaderQueueOnError = false } = options;
  const loading = ref(false);
  const loadingUploadUrls = ref(false);
  const postFiles = ref<Array<ContentFile | FileUploadData>>([]);
  const { getBlobUrl, revokeBlob } = useBlobUrl();
  const originalPostFilesIds = ref<string[]>([]);
  const fileDeleteQueue = ref<Function[]>([]);
  const fileCreateQueue = ref<DeferredFileCreate[]>([]);
  const uploadReplacementIds = new Map<string, string>();
  const errorUploadingFile = ref(false);
  const generateFileData = ref<GenerateFileData[]>([]);
  const pendingUploadIds = ref<string[]>([]);

  const markUploadStarted = (fileId: string) => {
    if (!pendingUploadIds.value.includes(fileId)) {
      pendingUploadIds.value = [...pendingUploadIds.value, fileId];
    }
  };

  const markUploadFinished = (fileId: string) => {
    pendingUploadIds.value = pendingUploadIds.value.filter(id => id !== fileId);
  };

  const postFileDataList = computed<PostFileData[]>(() => {
    return postFiles.value.map(i => {
      if (isFileUploadData(i)) {
        return {
          id: i.id,
          mediaType: i.mediaType ?? getFileMimeType(i.file),
          fileName: i.file.name,
          file: i.file,
          size: i.file.size,
          public: i.publicAccess,
          uploading: true,
          duration: null,
          url: i.blobUrl ?? "",
          uploadProgress: i.uploadProgress,
          processingStatus: "NEW",
          thumbnailType: i.thumbnailType,
          metadata: {},
        };
      } else {
        return {
          id: i.id,
          mediaType: i.mediaType,
          fileName: i.title,
          size: i.size,
          public: i.publicAccess,
          uploading: false,
          duration: i.duration,
          url: i.urlPart,
          uploadProgress: 100,
          processingStatus: i.processingStatus,
          thumbnailType: i.thumbnailType,
          metadata: "metadata" in i ? i["metadata"] : {},
        };
      }
    });
  });

  const isUploadingFiles = computed(() => postFileDataList.value.some(i => i.uploading));
  const uploadedFilesNumber = computed(() => postFileDataList.value.filter(i => !i.uploading).length);

  const onFileRemove = async (fileId: string) => {
    if (!postId.value) return;
    const file = postFiles.value.find(f => f.id === fileId);
    if (!file) return;

    postFiles.value = postFiles.value.filter(i => i.id !== fileId);
    if (isFileUploadData(file) && file.blobUrl) {
      revokeBlob(file.blobUrl);
    }
    if (isFileUploadData(file)) {
      markUploadFinished(fileId);
      FileUploader.removeFromQueue(fileId);
    } else if (defferChange.value) {
      fileDeleteQueue.value.push(() => providers.ContentFilesProvider.delete({ setId: postId.value, id: fileId }));
    } else {
      await providers.ContentFilesProvider.delete({ setId: postId.value, id: fileId });
    }
  };

  const stopAndRemoveUploadingFiles = async () => {
    postFiles.value.forEach(i => isFileUploadData(i) && onFileRemove(i.id));
  };

  const removeAllFiles = async () => {
    await Promise.all(postFiles.value.map(i => onFileRemove(i.id)));
  };

  const waitFileUpload = async (): Promise<void> => {
    return new Promise(resolve => {
      const pendingUploads = computed(() =>
        ignoreRemovedUploadSuccess
          ? pendingUploadIds.value.length
          : postFileDataList.value.filter(file => file.uploading).length
      );
      if (!pendingUploads.value) {
        resolve();
        return;
      }
      const unwatch = watch(pendingUploads, () => {
        if (!pendingUploads.value) {
          unwatch();
          resolve();
        }
      });
    });
  };

  const setPostFileSorting = async (postId: string) => {
    if (!postFiles.value.find(i => isFileUploadData(i))) {
      for (const [index, file] of postFiles.value.entries()) {
        await providers.ContentFilesProvider.update({
          setId: postId,
          data: {
            id: file.id,
            sort: index,
          },
        });
      }
    }
  };

  const deleteFilesFromQueue = async () => {
    for (const cb of fileDeleteQueue.value) {
      await cb();
    }
    fileDeleteQueue.value = [];
  };

  const createFilesFromQueue = async () => {
    for (const operation of fileCreateQueue.value) {
      await operation.run();
    }
    fileCreateQueue.value = [];
  };

  const resetUnsavedFilesQueue = async () => {
    fileCreateQueue.value = [];
  };

  const removeDeferredFileCreate = (fileId: string) => {
    fileCreateQueue.value = fileCreateQueue.value.filter(operation => operation.fileId !== fileId);
  };

  const onFileUploaded = async (
    { id, uuid, file, postId, metadata, mediaType, signedUrlPart }: UploadedFileData,
    afterFileUploadCB?: (file?: ContentFile) => void
  ) => {
    if (ignoreRemovedUploadSuccess && !postFiles.value.some(i => i.id === id)) {
      markUploadFinished(id);
      return;
    }

    const getInputFileData = (): InputContentFileCreate => {
      const currentPublicAccess = postFiles.value.find(i => i.id === id)?.publicAccess ?? false;
      const currentThumbnailType = postFiles.value.find(i => i.id === id)?.thumbnailType;
      return {
        title: file.name,
        name: file.name,
        uuid,
        publicAccess: currentPublicAccess,
        thumbnailType: !!currentThumbnailType ? currentThumbnailType : undefined,
        metadata,
      };
    };

    let contentFile: ContentFile;
    const data = getInputFileData();

    if (defferChange.value) {
      fileCreateQueue.value.push({
        fileId: id,
        run: async () => {
          const data = getInputFileData();
          const file = await providers.ContentFilesProvider.create({ setId: postId, data });
          postFiles.value = postFiles.value.map(i => (i.id === id ? file : i));
        },
      });
      let metadata = { duration: 0, width: 0, height: 0 };
      if (isVideoFile(file) || isAudioFile(file)) {
        metadata = await getBlobMetadata(file);
      }
      contentFile = getTemporaryContentFile({
        id,
        mediaType,
        name: file.name,
        publicAccess: data.publicAccess,
        setId: postId,
        size: file.size,
        sort: postFiles.value.length,
        title: file.name,
        urlPart: signedUrlPart,
        thumbnailType: data.thumbnailType,
        uuid,
        ...metadata,
      });
    } else {
      contentFile = await providers.ContentFilesProvider.create({ setId: postId, data });
    }
    const uploadingFile = postFiles.value.find(i => i.id === id);
    uploadReplacementIds.set(id, contentFile.id);
    postFiles.value = postFiles.value.map(i => (i.id === id ? contentFile : i));
    if (uploadingFile && isFileUploadData(uploadingFile) && uploadingFile.blobUrl) {
      revokeBlob(uploadingFile.blobUrl);
    }
    afterFileUploadCB && afterFileUploadCB(contentFile);
    if (!defferChange.value) await setPostFileSorting(postId);
    markUploadFinished(id);
    uploadReplacementIds.delete(id);
  };

  const removeFailedUploadingFile = (uploadId: string, renderedFileId = uploadId) => {
    markUploadFinished(uploadId);
    removeDeferredFileCreate(uploadId);
    const file = postFiles.value.find(f => f.id === renderedFileId || f.id === uploadId);
    if (!file) return;
    postFiles.value = postFiles.value.filter(i => i.id !== file.id);
    if (isFileUploadData(file) && file.blobUrl) revokeBlob(file.blobUrl);
    if (!isFileUploadData(file) && file.id !== uploadId && !defferChange.value) {
      void providers.ContentFilesProvider.delete({ setId: postId.value, id: file.id }).catch(error => {
        logger.debug("Failed to remove file after upload finalization error", { fileId: file.id, error });
      });
    }
    uploadReplacementIds.delete(uploadId);
  };

  const discardSettledUploadingFiles = () => {
    const staleIds = postFiles.value
      .filter(file => isFileUploadData(file) && !pendingUploadIds.value.includes(file.id))
      .map(file => file.id);
    if (!staleIds.length) return [];

    const staleFiles = postFileDataList.value.filter(file => staleIds.includes(file.id));
    postFiles.value.forEach(file => {
      if (staleIds.includes(file.id) && isFileUploadData(file) && file.blobUrl) {
        revokeBlob(file.blobUrl);
      }
    });
    postFiles.value = postFiles.value.filter(file => !staleIds.includes(file.id));
    return staleFiles;
  };

  const onAddFiles = async (
    files: FileList | File[],
    { unshift = false, publicAccess = false, metadata = {}, cb, thumbnailType, validatedMediaType }: AddingFilesOptions,
    afterFileUploadCB?: (file?: ContentFile) => void,
    onError?: () => void
  ) => {
    if (!postId.value) return;
    try {
      loadingUploadUrls.value = true;
      if (postType.value === ContentFeedTypeEnum.STORY && postFiles.value.length) {
        await onFileRemove(postFiles.value[0].id);
      }
      const uploadUrls = await providers.ContentFilesProvider.getUploadUrls({
        setId: postId.value,
        count: files.length,
      });

      const uploadData: UploadFileData[] = [];
      Array.from(files).forEach((file, index) => {
        const { uuid, urlPart, signedUrlPart } = uploadUrls[index];
        const id = TEMP_FILE_PREFIX + uuid;
        const blobUrl = getBlobUrl(file);
        const mediaType = validatedMediaType ?? getFileMimeType(file);
        postFiles.value[unshift ? "unshift" : "push"]({
          id,
          file,
          publicAccess,
          thumbnailType,
          mediaType,
          uploadProgress: 0,
          timeUntilSuccess: 0,
          blobUrl,
        });
        markUploadStarted(id);
        const data: UploadFileData = {
          urlPart: urlPart,
          fileType: mediaType,
          file,
          id,
          onSuccess: () =>
            onFileUploaded(
              { id, postId: postId.value, file, uuid, metadata, mediaType, signedUrlPart },
              afterFileUploadCB
            ),
          onUploadProgress: p => {
            const index = postFiles.value.findIndex(i => i.id === id && isFileUploadData(i));
            if (index !== -1) {
              set(postFiles.value[index], "uploadProgress", calculateProgress(p));
            }
          },
          onError: e => {
            const wasPending = pendingUploadIds.value.includes(data.id);
            const renderedFileId = uploadReplacementIds.get(data.id) ?? data.id;
            markUploadFinished(data.id);
            const hasRenderedFile = postFiles.value.some(i => i.id === data.id || i.id === renderedFileId);
            // FileUploader reports errors from async onSuccess finalization with the original upload ID.
            // By then the UI entry may already use the backend ContentFile ID or may have been removed.
            if (!hasRenderedFile && !wasPending && !uploadReplacementIds.has(data.id)) return;
            errorUploadingFile.value = true;
            onError && onError();
            logger.debug("Upload error", { dataId: data.id, dataPartUrl: data.urlPart, error: e });
            console.error(e);
            showDefaultUploadError();
            if (keepUploaderQueueOnError) {
              // Full Creator lets sibling workers finish before publishing the valid posts.
              removeFailedUploadingFile(data.id, renderedFileId);
            } else {
              // Preserve the established PostCreate cancellation/removal behavior.
              onFileRemove(renderedFileId);
            }
            uploadReplacementIds.delete(data.id);
          },
        };
        uploadData.push(data);
      });
      FileUploader.addToQueue(uploadData);
    } finally {
      loadingUploadUrls.value = false;
      cb?.();
    }
  };

  const onAddFilesFromVault = async (files: AttachmentFile[], options?: AddingFilesOptions) => {
    if (!postId.value) return;
    try {
      if (postType.value === ContentFeedTypeEnum.STORY && postFiles.value.length) {
        await onFileRemove(postFiles.value[0].id);
      }
      for (const file of files) {
        let resultFile: ContentFile;
        if (defferChange.value) {
          const tempFileId = `${TEMP_FILE_PREFIX}${file.uid}${TEMP_FILE_POSTFIX}${Date.now()}`;
          fileCreateQueue.value.push({
            fileId: tempFileId,
            run: async () => {
              const resultFile = await providers.ContentFilesProvider.create({
                setId: postId.value,
                data: {
                  name: file.metadata?.filename ?? file.uid,
                  uuid: file.uid,
                  publicAccess: options?.publicAccess ?? false,
                  thumbnailType: options?.thumbnailType,
                  title: file.metadata?.filename ?? file.uid,
                  ...(options?.metadata?.stereoFormat
                    ? { metadata: { stereoFormat: options.metadata.stereoFormat } }
                    : {}),
                },
              });
              postFiles.value = postFiles.value.map(i =>
                i.id === tempFileId
                  ? {
                      ...resultFile,
                      publicAccess: "publicAccess" in i ? i.publicAccess : false,
                      ...("metadata" in i && i.metadata.stereoFormat ? { stereoFormat: i.metadata.stereoFormat } : {}),
                    }
                  : i
              );
            },
          });
          resultFile = getTemporaryContentFile({
            id: tempFileId,
            mediaType: file.type,
            name: file.metadata?.filename ?? file.uid,
            publicAccess: options?.publicAccess ?? false,
            thumbnailType: options?.thumbnailType,
            setId: postId.value,
            sort: postFiles.value.length,
            title: file.metadata?.filename ?? file.uid,
            urlPart: file.src,
            uuid: file.uid,
            duration: +(file.metadata?.duration || 0),
            metadata: {
              ...file.metadata,
              ...(options?.metadata?.stereoFormat ? { stereoFormat: options.metadata.stereoFormat } : {}),
            },
          });
        } else {
          resultFile = await providers.ContentFilesProvider.create({
            setId: postId.value,
            data: {
              name: file.metadata?.filename ?? file.uid,
              uuid: file.uid,
              publicAccess: options?.publicAccess ?? false,
              thumbnailType: options?.thumbnailType,
              title: file.metadata?.filename ?? file.uid,
              ...(options?.metadata?.stereoFormat ? { metadata: { stereoFormat: options.metadata.stereoFormat } } : {}),
            },
          });
        }
        postFiles.value[options?.unshift ? "unshift" : "push"](resultFile);
      }
    } finally {
      options?.cb?.();
    }
  };

  const onFileChange = async (data: InputContentFileUpdate) => {
    postFiles.value = postFiles.value.map(i => (i.id === data.id ? { ...i, ...data } : i));
  };

  const setPostFilePublicAccess = async (postId: string) => {
    const filesUpdatePromises = postFiles.value.map(
      file =>
        (!isFileUploadData(file) &&
          providers.ContentFilesProvider.update({
            setId: postId,
            data: {
              id: file.id,
              publicAccess: file.publicAccess,
              metadata: file.metadata,
            },
          })) ||
        undefined
    );
    await Promise.all<void | ContentFile>(filesUpdatePromises);
  };

  const onDraggableSort = async (sortedIds: string[]) => {
    const idToIndexMap = new Map<string, number>();
    sortedIds.forEach((id, index) => {
      idToIndexMap.set(id, index);
    });
    const unorderedFiles = [...postFiles.value];
    postFiles.value = postFiles.value.sort((a, b) => {
      const indexA = idToIndexMap.get(a.id) ?? Infinity;
      const indexB = idToIndexMap.get(b.id) ?? Infinity;
      return indexA - indexB;
    });
    try {
      if (!defferChange.value) await setPostFileSorting(postId.value);
    } catch (e) {
      postFiles.value = unorderedFiles;
    }
  };

  const requestAndSetPostFiles = async (postId: string) => {
    return providers.ContentFilesProvider.search({
      data: {
        setId: [postId],
        size: 9999,
        sort: "SORT,ASC",
      },
    }).then(resp => {
      postFiles.value = resp.result;
      originalPostFilesIds.value = postFiles.value.map(file => file.id);
    });
  };

  const getPostFiles = async (postId: string) => {
    try {
      loading.value = true;
      if (postId) {
        await requestAndSetPostFiles(postId);
      }
    } finally {
      loading.value = false;
    }
  };

  const addRequestGenerateFile = (data: GenerateFileData) => {
    const uid = extractUid(data.id);
    if (!uid) return;
    generateFileData.value = generateFileData.value.filter(i => i.id !== uid);
    generateFileData.value.push({ ...data, id: uid });
  };

  const removeRequestGenerateFile = (id: string) => {
    const uid = extractUid(id);
    if (!uid) return;
    generateFileData.value = generateFileData.value.filter(i => i.id !== uid);
  };

  const requestGenerateFile = () => {
    if (!postId.value) return;
    return Promise.all(
      generateFileData.value.map(async i => {
        const { id, ...data } = i;

        const file = postFiles.value.find(f => [(f as ContentFile).uuid, f.id].includes(id));
        if (!file || !("uuid" in file)) return;
        return providers.ContentFilesProvider.createTrailer({
          data: {
            setId: postId.value,
            sourceFileUuid: file.uuid,
            ...data,
          },
        });
      })
    );
  };

  const updatePostFiles = async () => {
    if (!postId.value) return;
    const promises = [deleteFilesFromQueue()];
    await waitFileUpload();
    promises.push(createFilesFromQueue());
    await Promise.all(promises);
    // Persist public access before generating the trailer: trailer generation resolves its
    // source by the main video's (publicAccess=false) state, so it must be settled first.
    await setPostFilePublicAccess(postId.value);
    await setPostFileSorting(postId.value);
    await requestGenerateFile();
  };

  const reset = () => {
    stopAndRemoveUploadingFiles().then();
    resetUnsavedFilesQueue().then();
    generateFileData.value = [];
  };

  return {
    postFiles,
    errorUploadingFile,
    loadingUploadUrls,
    postFileDataList,
    originalPostFilesIds,
    isUploadingFiles,
    uploadedFilesNumber,
    generateFileData,
    waitFileUpload,
    getPostFiles,
    updatePostFiles,
    onAddFiles,
    onAddFilesFromVault,
    onFileRemove,
    onFileChange,
    stopAndRemoveUploadingFiles,
    discardSettledUploadingFiles,
    removeAllFiles,
    resetUnsavedFilesQueue,
    onDraggableSort,
    addRequestGenerateFile,
    removeRequestGenerateFile,
    reset,
  };
}
